import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Container from '@material-ui/core/Container';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
// import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import PasswordField from '@sprinx/react-mui-fields/PasswordField';
import TextField from '@sprinx/react-mui-fields/TextField';
import qs from 'qs';
import React from 'react';
import { useSetRecoilState } from 'recoil';
import { AfterPageProps } from '../../@sprinx/react-after-razzle';
import { useApiClient } from '../../api/appState';
import useLoginHandler from '../../api/auth/useLoginHandler';
import { customerNotificationOpenState } from '../../api/customerNotification';
import { enqueueSnackbarMessage } from '../../api/snackbars';
import legalPages from '../../legalPages';
import LoginPageadvantageSection from './components/LoginPageadvantageSection';
import LoginPageInformationSection from './components/LoginPageInformationSection';
import LoginPageLogo from './LoginPageLogo';

export type LoginPageProps = AfterPageProps;
export type LoginPageClassNameKey =
  | 'root'
  | 'content'
  | 'headline'
  | 'fields'
  | 'actions'
  // | 'logo'
  | 'loginButton'
  | 'forgotFieldsContainer'
  | 'legalLinks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles<LoginPageClassNameKey, {}>({
    root: {
      paddingTop: theme.spacing(15),
      [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(1),
      },
    },
    content: {
      boxShadow: theme.shadows[4],
    },
    headline: {
      backgroundColor: 'white',
      padding: theme.spacing(2.5, 3),
      display: 'flex',
      justifyContent: 'center',
    },
    fields: {
      padding: theme.spacing(2, 3),
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      padding: theme.spacing(2, 3),
      '& > a': {
        cursor: 'pointer',
        marginRight: 'auto',
      },
    },
    forgotFieldsContainer: {
      padding: theme.spacing(0, 3, 3, 3),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
    loginButton: {
      backgroundColor: '#69BF40',
      color: 'white',
      '&:hover': {
        backgroundColor: '#72c24a',
      },
    },
    legalLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5, 2),
      marginTop: theme.spacing(2),
    },
  }),
);

const initialValues = {
  username: '',
  password: '',
  forgotUser: '',
};

interface Values {
  forgotUser: string;
  password: string;
  username: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ location, history }) => {
  const classes = useStyles();
  const [values, setValues] = React.useState<Values>(initialValues);
  const [error, setError] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState<boolean>(false);
  const showMessage = useSetRecoilState(enqueueSnackbarMessage);
  const apiClient = useApiClient();
  const setNotificationOpen = useSetRecoilState(customerNotificationOpenState);

  const handleChange = React.useCallback(
    (fieldName: keyof Values) => (value: string | undefined) => {
      setValues((ps) => ({ ...ps, [fieldName]: value }));
    },
    [],
  );

  const handlePasswordReset = React.useCallback(() => {
    if (values.forgotUser) {
      apiClient
        .post<{ email: string }, { username: string }>('/v1/users/renew-password', {
          username: values.forgotUser,
        })
        .then(
          ({ email }) => {
            showMessage({ message: `Heslo bylo odesláno na Váš e-mail ${email}`, variant: 'success' });
            setValues(initialValues);
            setOpen(false);
          },
          () => {
            showMessage({ message: 'Nepodařilo se obnovit heslo.', variant: 'error' });
          },
        );

      // ApiClient
      // .then((res) => {
      //    showMessage({ message: 'Heslo bylo odesláno na Váš e-mail', variant: 'success' });
      //    setValues(initialValues);
      //    setOpen(false);
      // })
      // .catch(() => showMessage({ message: 'Neplatné uživatelské jméno', variant: 'error' }))
      // showMessage({ message: 'Heslo bylo odesláno na Váš e-mail', variant: 'success' });
    }
  }, [values, showMessage, apiClient]);

  const handleLogin = useLoginHandler(
    (auth) => {
      setNotificationOpen(true);

      const isHolding = auth.profile?.childUsers != null;

      let afterLoginPath = '';

      if (isHolding) {
        afterLoginPath = '/hlavni-nakupci';
      } else {
        afterLoginPath = location?.search
          ? (qs.parse(location?.search, { ignoreQueryPrefix: true })?.from as string)
          : '/catalogue';
      }
      history.replace(afterLoginPath);
    },
    (err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Login Error ', err);
      }
      setError(true);
    },
  );

  return (
    <Container className={classes.root}>
      <Container component='main' maxWidth='sm'>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleLogin(values.username, values.password);
          }}
        >
          <div className={classes.content}>
            <div className={classes.headline}>
              <LoginPageLogo />
            </div>
            <div className={classes.fields}>
              {error && (
                <Alert severity='error'>
                  <strong>Chyba přihlášení.</strong> Nesprávné jméno nebo heslo.
                </Alert>
              )}
              <TextField
                type='text'
                label='Uživatelské jméno'
                onChange={handleChange('username')}
                value={values.username}
                fullWidth
                margin='normal'
              />
              <PasswordField
                label='Heslo'
                onChange={handleChange('password')}
                value={values.password}
                fullWidth
                margin='normal'
              />
            </div>
            <div className={classes.actions}>
              <Link component={'a'} onClick={() => setOpen((ps) => !ps)}>
                Zapomenuté heslo
              </Link>
              <Button type='submit' className={classes.loginButton} variant='contained'>
                Přihlásit
              </Button>
            </div>
            <Collapse in={open}>
              <div className={classes.forgotFieldsContainer}>
                <TextField
                  type='text'
                  label='Zadejte svůj přihlašovací email'
                  onChange={handleChange('forgotUser')}
                  value={values.forgotUser}
                  fullWidth
                  margin='normal'
                />
                <Button variant='contained' color='primary' size='small' onClick={handlePasswordReset}>
                  Odeslat
                </Button>
              </div>
            </Collapse>
          </div>
        </form>
        {/*
          The rest of the portal is behind authentication, so this is the only place an
          anonymous visitor can reach these documents. They have to be reachable here
          because the cookie consent banner asks for a decision on this very page.
        */}
        <div className={classes.legalLinks}>
          <Link href={legalPages.privacyPolicy.href} target='_blank' rel='noopener noreferrer' variant='body2'>
            {legalPages.privacyPolicy.name}
          </Link>
          <Link href={legalPages.cookiePolicy.href} target='_blank' rel='noopener noreferrer' variant='body2'>
            {legalPages.cookiePolicy.name}
          </Link>
        </div>
      </Container>
      <LoginPageInformationSection />
      <LoginPageadvantageSection />
    </Container>
  );
};

LoginPage.displayName = 'LoginPage';

export default LoginPage;
